import { blue, FaroThemeOptions, neutral } from "@flat-ui";
import { checkboxClasses, createTheme, Theme, ThemeOptions } from "@mui/material";
import {
  DEFAULT_TEXT_FONT_SIZE,
  textfieldSx,
  inputPlaceholderItalicSx,
  withEllipsis,
} from "@styles/common-styles";
import { deepmerge } from "@mui/utils";
import { sphereColorsTransparent } from "@styles/common-colors";
// Empty import to augment MUI Data Grid Pro, otherwise it is not recognized as an MUI component
import type {} from "@mui/x-data-grid-pro/themeAugmentation";

/** Theme mode type for the application */
export type ThemeMode = "dark" | "light";

/* Custom theme options for the dashboard that are different than the LOTV package */
const dashboardThemeOptions: ThemeOptions = {
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            ...inputPlaceholderItalicSx,
          }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            ...textfieldSx,
          }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          [`&.${checkboxClasses.checked}, &.${checkboxClasses.indeterminate}`]:
            {
              color: blue[500],
            },
          "&:hover": {
            color: blue[500],
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: neutral[200],
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginTop: "10px",
          "& .MuiFormControlLabel-label": {
            fontSize: DEFAULT_TEXT_FONT_SIZE,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.MuiFormLabel-root": {
            fontSize: DEFAULT_TEXT_FONT_SIZE,
            fontWeight: 400,
            color: neutral[500],
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[50],
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          marginTop: "1px",
          padding: "0px",
          display: "flex",
          // Show the border on the last visible row
          "& .MuiDataGrid-row--lastVisible": {
            borderBottom: "1px solid rgba(224, 224, 224, 1)",
          },
          // Overwrite custom property that defines the header background color
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "--DataGrid-containerBackground": "transparent",
        },
        main: {
          flex: "initial",
        },
        row: {
          "&.Mui-selected": {
            backgroundColor: sphereColorsTransparent.gray50,
          },
          "& .MuiSelect-select": {
            // Remove left padding to dropdowns in table cells
            paddingLeft: "0",
          },
        },
        cell: {
          display: "flex",
          alignItems: "center",
          fontSize: "12px",
          color: sphereColorsTransparent.gray800,
          padding: "8px 10px",
          whiteSpace: "nowrap",
          ...withEllipsis,
          "&:focus-within": {
            outline: "none",
          },
          [`@media (max-width: ${"900px"})`]: {
            verticalAlign: "middle",
          },
        },
        checkboxInput: {
          padding: 0,
        },
        withBorderColor: {
          borderColor: "none",
        },

        toolbarContainer: {
          marginTop: "1px",
          padding: "0px",
        },
        columnHeader: {
          fontSize: "12px",
          color: sphereColorsTransparent.gray600,
          padding: "0 10px",
          "&:focus-within": {
            outline: "none",
          },
          "&.MuiDataGrid-columnHeader--sorted": {
            color: sphereColorsTransparent.gray800,
          },
        },
        columnSeparator: {
          display: "none",
        },
        sortIcon: {
          strokeWidth: "2",
          stroke: sphereColorsTransparent.gray800,
        },
      },
    },
  },
};

/** Creates a combined theme options with the flat-ui theme options and the dashboard specific theme options */
export const combinedThemeOptions = deepmerge(
  FaroThemeOptions,
  dashboardThemeOptions
);

/**
 * Merges the flat-ui theme with the dashboard specific theme options.
 * The dashboard specific theme options will override the flat-ui theme options.
 * @param mode The theme mode, either "light" or "dark". Default is "light".
 */
export function getTheme(mode: ThemeMode = "light"): Theme {
  return createTheme({
    ...combinedThemeOptions,
    palette: {
      ...combinedThemeOptions.palette,
      mode,
    },
  });
}
